import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  ViewChild,
  forwardRef,
  inject,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

/**
 * A text field that turns its entries into chips: Enter adds the text as a
 * chip, blur too with addOnBlur, Backspace in the empty field removes the last
 * chip, duplicates are left out unless allowDuplicate is set.
 */
@Component({
  selector: 'app-chips',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ChipsComponent),
      multi: true,
    },
  ],
  template: `<!-- A click next to the tokens focuses the input, which keyboard users reach directly. -->
    <!-- eslint-disable-next-line @angular-eslint/template/click-events-have-key-events, @angular-eslint/template/interactive-supports-focus -->
    <div
      class="app-chips {{ styleClass }}"
      [class.app-chips-clearable]="showClear && !disabled"
      (click)="input.focus()"
    >
      <ul
        class="app-input app-chips-multiple-container"
        [class.app-focus]="focused"
        [class.app-disabled]="disabled"
      >
        @for (item of value || []; track $index) {
          <li class="app-chips-token">
            <span class="app-chips-token-label">{{ item }}</span>
            @if (!disabled) {
              <span
                class="app-chips-token-icon app-icon fa-solid fa-circle-xmark"
                aria-hidden="true"
                (click)="remove($event, $index)"
              ></span>
            }
          </li>
        }
        <li class="app-chips-input-token">
          <input
            #input
            type="text"
            [attr.placeholder]="value?.length ? null : placeholder"
            [attr.aria-label]="ariaLabel || placeholder"
            [disabled]="disabled"
            (keydown)="onKeydown($event)"
            (focus)="focused = true"
            (blur)="onBlur()"
          />
        </li>
        <li>
          @if (showClear && value?.length && !disabled) {
            <i
              class="app-chips-clear-icon app-icon fa-solid fa-xmark"
              aria-hidden="true"
              (click)="clear($event)"
            ></i>
          }
        </li>
      </ul>
    </div>`,
})
export class ChipsComponent implements ControlValueAccessor {
  private cdr = inject(ChangeDetectorRef);

  @Input() placeholder?: string;
  @Input() addOnBlur = false;
  @Input() allowDuplicate = true;
  @Input() showClear = false;
  @Input() styleClass = '';
  @Input() ariaLabel?: string;
  @Input() disabled = false;

  @ViewChild('input', { static: true })
  private input!: ElementRef<HTMLInputElement>;

  value: string[] | null = null;
  focused = false;

  private onChange: (value: string[]) => void = () => undefined;
  private onTouched: () => void = () => undefined;

  writeValue(value: string[] | null): void {
    this.value = value;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    this.disabled = disabled;
    this.cdr.markForCheck();
  }

  onKeydown(event: KeyboardEvent) {
    const text = this.input.nativeElement.value;
    if (event.key === 'Enter') {
      event.preventDefault();
      this.add(text);
    } else if (event.key === 'Backspace' && !text && this.value?.length) {
      this.setValue(this.value.slice(0, -1));
    }
  }

  onBlur() {
    this.focused = false;
    if (this.addOnBlur && this.input.nativeElement.value) {
      this.add(this.input.nativeElement.value);
    }
    this.onTouched();
  }

  remove(event: Event, index: number) {
    event.stopPropagation();
    this.setValue((this.value || []).filter((_, i) => i !== index));
  }

  // An empty list, not null, keeps the callers' `.length` safe.
  clear(event: Event) {
    event.stopPropagation();
    this.setValue([]);
  }

  private add(text: string) {
    const values = this.value || [];
    if (text.trim() && (this.allowDuplicate || !values.includes(text))) {
      this.setValue([...values, text]);
    }
    this.input.nativeElement.value = '';
  }

  private setValue(value: string[]) {
    this.value = value;
    this.onChange(value);
    this.cdr.markForCheck();
  }
}
